/**
 * Appearance catalog: the single source of truth for what the user can pick in
 * Preferences -> Appearance (theme, accent colour, background, texture, glass
 * effect and font).
 *
 * Pure data, deliberately free of Angular imports so services, components and
 * specs can all read it. Ported from MeetingRec's `appearance/presets.ts`; the
 * user-facing names live in the i18n catalogs, not here.
 */

/** Light/dark mode choice. `system` follows the operating system. */
export type ThemeChoice = 'system' | 'light' | 'dark';

/** Theme choices in the order the picker lists them. */
export const THEME_CHOICES = ['system', 'light', 'dark'] as const;

/**
 * The groups the accent picker shows as tabs, in order (Daniel, 2026-10-19:
 * basic first, it holds the default):
 * - basic: the colours anyone names, Azul (the default) first.
 * - modern: pigment and gem names (Indigo, Emerald...).
 * - metallic: named after a metal.
 */
export const ACCENT_CATEGORIES = ['basic', 'modern', 'metallic'] as const;
export type AccentCategory = (typeof ACCENT_CATEGORIES)[number];

/**
 * One accent colour. `light` and `dark` are the brand hex per colour scheme.
 * Flags, all optional:
 * - `flatNight`: a shallow night ramp for hues that turn brown as they darken.
 * - `neutral`: a near-grey preset, so its night surface is not required to carry visible chroma.
 * - `whiteTextMin`: a preset allowed below AA for white text on its fills (contrast ratio floor).
 */
export interface AccentPreset {
  id: string;
  category: AccentCategory;
  light: string;
  dark: string;
  flatNight?: boolean;
  whiteTextMin?: number;
  neutral?: boolean;
}

const ACCENT_DATA = [
  // Basics go most used first, Azul (the default accent) leading.
  { id: 'blue', category: 'basic', light: '#0c66e4', dark: '#579dff' },
  { id: 'graphite', category: 'basic', light: '#171717', dark: '#e5e5e5' },
  { id: 'green', category: 'basic', light: '#278827', dark: '#62cd5e' },
  { id: 'red', category: 'basic', light: '#c9372c', dark: '#f45a56' },
  { id: 'violet', category: 'basic', light: '#6e5dc6', dark: '#9f8fef' },
  { id: 'orange', category: 'basic', light: '#e06a00', dark: '#fea362', flatNight: true },
  { id: 'pink', category: 'basic', light: '#e0608a', dark: '#ffa3c0' },
  {
    id: 'yellow',
    category: 'basic',
    light: '#d4a106',
    dark: '#ffd84d',
    flatNight: true,
    whiteTextMin: 3,
  },
  { id: 'brown', category: 'basic', light: '#7b5233', dark: '#b89a86' },
  // Gold is a yellow, which turns brown as it darkens; its night ramp must be dark
  // enough for white text, so it gets a flat ramp kept as light as that allows.
  { id: 'amber', category: 'metallic', light: '#b38600', dark: '#d9b25c', flatNight: true },
  // Metallics in Daniel's order: Dorado, Plata, Bronce, Oro rosa, Cobre, Titanio,
  // Champán, Acero, Cobalto (ids kept from MeetingRec: amber is Dorado, gunmetal Titanio).
  { id: 'silver', category: 'metallic', light: '#6f7b88', dark: '#c3ccd6', neutral: true },
  { id: 'bronze', category: 'metallic', light: '#9c6b30', dark: '#dc9450', flatNight: true },
  { id: 'rosegold', category: 'metallic', light: '#b76e79', dark: '#d9ab9f' },
  { id: 'copper', category: 'metallic', light: '#ad5437', dark: '#c8825f' },
  { id: 'gunmetal', category: 'metallic', light: '#3a3f47', dark: '#9fa9b8', neutral: true },
  { id: 'champagne', category: 'metallic', light: '#928466', dark: '#e0cc9e', neutral: true },
  { id: 'steel', category: 'metallic', light: '#3f5f7a', dark: '#6f9ccc', neutral: true },
  { id: 'cobalt', category: 'metallic', light: '#2a2f8f', dark: '#5f7cff' },
  // Obsidian first in modern, then the spectrum: green, blue, violet, pink, red.
  { id: 'obsidian', category: 'modern', light: '#181534', dark: '#6e5ae6' },
  { id: 'emerald', category: 'modern', light: '#008662', dark: '#4fcb9c' },
  { id: 'teal', category: 'modern', light: '#227d9b', dark: '#6cc3e0' },
  { id: 'navy', category: 'modern', light: '#09326c', dark: '#85b8ff' },
  { id: 'indigo', category: 'modern', light: '#4a1fd9', dark: '#445fb5' },
  { id: 'lavender', category: 'modern', light: '#8b7fd4', dark: '#c4b5fd' },
  { id: 'plum', category: 'modern', light: '#7e2d7a', dark: '#d38bd0' },
  { id: 'magenta', category: 'modern', light: '#b229ad', dark: '#e068d8' },
  { id: 'wine', category: 'modern', light: '#7a1f3d', dark: '#c9788f' },
] as const;

/** Id of any accent in the catalog. */
export type AccentId = (typeof ACCENT_DATA)[number]['id'];

/** Every accent the user can pick, in picker order. */
export const ACCENT_PRESETS: readonly AccentPreset[] = ACCENT_DATA;

/** Accent ids in catalog order. */
export const ACCENT_IDS: readonly AccentId[] = ACCENT_DATA.map((preset) => preset.id);

/**
 * A static mesh background: five stops per scheme (base first, then the four
 * corner blobs) so a preview tile never inherits a stop from the live page.
 * `texture` adds the fine grain.
 */
export interface BackgroundPreset {
  id: string;
  light: readonly string[];
  dark: readonly string[];
  texture: boolean;
}

const BACKGROUND_DATA = [
  {
    id: 'quiet-halo',
    light: ['#f5f5fa', '#dfe4ed', '#bdd3dc', '#d5cfea', '#bdd3dc'],
    dark: ['#080a11', '#111827', '#264a61', '#5a4f8e', '#264a61'],
    texture: true,
  },
  {
    id: 'aurora-mesh',
    light: ['#eefaf7', '#a8ead8', '#9bdcf2', '#c6f4bd', '#d5cdf2'],
    dark: ['#05090d', '#00cda8', '#00a8e8', '#6cff70', '#5135a8'],
    texture: true,
  },
  {
    id: 'northern-veil',
    light: ['#eefbf6', '#9ce2c4', '#a0e8df', '#aec9ec', '#c5f3d0'],
    dark: ['#030a0d', '#00bd78', '#14d7c8', '#2069c9', '#6ef59b'],
    texture: true,
  },
  {
    id: 'lagoon',
    light: ['#effbf8', '#acdcd3', '#9de3d4', '#aed9e8', '#b6eddf'],
    dark: ['#020b0c', '#006c62', '#00b79a', '#087eaa', '#35d6b1'],
    texture: true,
  },
  {
    id: 'deep-current',
    light: ['#edf6fa', '#b9d8e5', '#9cddd8', '#a7d2ec', '#d3deed'],
    dark: ['#020711', '#063a5b', '#00a7a5', '#0078b8', '#0b1538'],
    texture: false,
  },
  {
    id: 'polar-mist',
    light: ['#f5fbfc', '#c8eef4', '#c4d6e8', '#dcd7ed', '#e0f5f0'],
    dark: ['#071016', '#76d5e7', '#5b88ba', '#a89fd5', '#d1f4ec'],
    texture: false,
  },
  {
    id: 'electric-bloom',
    light: ['#f6f2ff', '#aebcff', '#c3b2ff', '#f0addf', '#a9e5ee'],
    dark: ['#05040d', '#1b3fff', '#7036ff', '#e12abf', '#0cc7e8'],
    texture: false,
  },
  {
    id: 'ultraviolet',
    light: ['#faf3ff', '#cfc1eb', '#c7b3ed', '#e5b5e5', '#f4bfd6'],
    dark: ['#05030d', '#21105c', '#5d28bd', '#b522b9', '#ef4f9b'],
    texture: true,
  },
  {
    id: 'rose-quartz',
    light: ['#fff5f8', '#f1c4d4', '#f3b7c7', '#f8ccd9', '#e2c4e5'],
    dark: ['#0c0508', '#4a1130', '#a32350', '#e05a86', '#7a2f6d'],
    texture: true,
  },
  {
    id: 'ember-glass',
    light: ['#fff7f2', '#e9bcc3', '#edb19e', '#f4d29e', '#dbc7d8'],
    dark: ['#0a0608', '#5e1728', '#b74525', '#f0a34a', '#382038'],
    texture: true,
  },
  {
    id: 'solar-drift',
    light: ['#fff9f1', '#e8c2c4', '#efc09e', '#f2d9a2', '#d9c4dd'],
    dark: ['#090608', '#6b2f34', '#d0702e', '#e3b65a', '#47224c'],
    texture: false,
  },
] as const;

/** The mesh backgrounds, without the `none` option. */
export const BACKGROUND_PRESETS: readonly BackgroundPreset[] = BACKGROUND_DATA;

/** Background ids, `none` first. */
export const BACKGROUND_IDS = ['none', ...BACKGROUND_DATA.map((preset) => preset.id)] as const;
export type BackgroundId = (typeof BACKGROUND_IDS)[number];

/**
 * How strongly a background's stops show over the ground, per scheme. Dark is
 * lower because its stops are bright on near-black.
 */
export const MESH_ALPHA = { light: 0.78, dark: 0.45 } as const;

/**
 * Textures: a faint repeating motif laid over the background, `none` included.
 * Eleven plus `none`, so the picker's rows of two, three or four are always full.
 */
export const PATTERN_IDS = [
  'none',
  'dots',
  'grid',
  'crosses',
  'pinstripe',
  'isometric',
  'honeycomb',
  'waves',
  'seigaiha',
  'topography',
  'diamonds',
  'grain',
] as const;
export type PatternId = (typeof PATTERN_IDS)[number];

/** Font groups, in the order the catalog lists them. */
export const FONT_CATEGORIES = ['sans', 'serif', 'mono', 'display', 'rounded'] as const;
export type FontCategory = (typeof FONT_CATEGORIES)[number];

/**
 * One selectable font. `google` is the Google Fonts family to download, `null`
 * when nothing is downloaded. `name` is `null` for the platform font, which is
 * named at runtime.
 */
export interface FontPreset {
  id: string;
  name: string | null;
  family: string;
  category: FontCategory;
  google: string | null;
}

const FONT_DATA = [
  { id: 'geist', name: 'Geist', family: "'Geist'", category: 'sans', google: 'Geist' },
  {
    id: 'system',
    name: null,
    family: 'ui-sans-serif, -apple-system, "Segoe UI", Roboto',
    category: 'sans',
    google: null,
  },
  { id: 'inter', name: 'Inter', family: "'Inter'", category: 'sans', google: 'Inter' },
  { id: 'manrope', name: 'Manrope', family: "'Manrope'", category: 'sans', google: 'Manrope' },
  {
    id: 'ibm-plex-sans',
    name: 'IBM Plex Sans',
    family: "'IBM Plex Sans'",
    category: 'sans',
    google: 'IBM Plex Sans',
  },
  {
    id: 'plus-jakarta-sans',
    name: 'Plus Jakarta Sans',
    family: "'Plus Jakarta Sans'",
    category: 'sans',
    google: 'Plus Jakarta Sans',
  },
  {
    id: 'space-grotesk',
    name: 'Space Grotesk',
    family: "'Space Grotesk'",
    category: 'sans',
    google: 'Space Grotesk',
  },
  {
    id: 'atkinson',
    name: 'Atkinson Hyperlegible',
    family: "'Atkinson Hyperlegible Next'",
    category: 'sans',
    google: 'Atkinson Hyperlegible Next',
  },
  { id: 'poppins', name: 'Poppins', family: "'Poppins'", category: 'sans', google: 'Poppins' },
  {
    id: 'montserrat',
    name: 'Montserrat',
    family: "'Montserrat'",
    category: 'sans',
    google: 'Montserrat',
  },
  { id: 'roboto', name: 'Roboto', family: "'Roboto'", category: 'sans', google: 'Roboto' },
  { id: 'onest', name: 'Onest', family: "'Onest'", category: 'sans', google: 'Onest' },
  { id: 'lato', name: 'Lato', family: "'Lato'", category: 'sans', google: 'Lato' },
  { id: 'nunito', name: 'Nunito', family: "'Nunito'", category: 'sans', google: 'Nunito' },
  {
    id: 'work-sans',
    name: 'Work Sans',
    family: "'Work Sans'",
    category: 'sans',
    google: 'Work Sans',
  },
  { id: 'dm-sans', name: 'DM Sans', family: "'DM Sans'", category: 'sans', google: 'DM Sans' },
  { id: 'sora', name: 'Sora', family: "'Sora'", category: 'sans', google: 'Sora' },
  { id: 'outfit', name: 'Outfit', family: "'Outfit'", category: 'sans', google: 'Outfit' },
  { id: 'figtree', name: 'Figtree', family: "'Figtree'", category: 'sans', google: 'Figtree' },
  { id: 'archivo', name: 'Archivo', family: "'Archivo'", category: 'sans', google: 'Archivo' },
  { id: 'lexend', name: 'Lexend', family: "'Lexend'", category: 'sans', google: 'Lexend' },
  { id: 'raleway', name: 'Raleway', family: "'Raleway'", category: 'sans', google: 'Raleway' },
  { id: 'lora', name: 'Lora', family: "'Lora'", category: 'serif', google: 'Lora' },
  {
    id: 'playfair-display',
    name: 'Playfair Display',
    family: "'Playfair Display'",
    category: 'serif',
    google: 'Playfair Display',
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    family: "'Merriweather'",
    category: 'serif',
    google: 'Merriweather',
  },
  {
    id: 'source-serif-4',
    name: 'Source Serif 4',
    family: "'Source Serif 4'",
    category: 'serif',
    google: 'Source Serif 4',
  },
  { id: 'fraunces', name: 'Fraunces', family: "'Fraunces'", category: 'serif', google: 'Fraunces' },
  {
    id: 'eb-garamond',
    name: 'EB Garamond',
    family: "'EB Garamond'",
    category: 'serif',
    google: 'EB Garamond',
  },
  {
    id: 'libre-baskerville',
    name: 'Libre Baskerville',
    family: "'Libre Baskerville'",
    category: 'serif',
    google: 'Libre Baskerville',
  },
  {
    id: 'crimson-pro',
    name: 'Crimson Pro',
    family: "'Crimson Pro'",
    category: 'serif',
    google: 'Crimson Pro',
  },
  {
    id: 'instrument-serif',
    name: 'Instrument Serif',
    family: "'Instrument Serif'",
    category: 'serif',
    google: 'Instrument Serif',
  },
  {
    id: 'dm-serif-display',
    name: 'DM Serif Display',
    family: "'DM Serif Display'",
    category: 'serif',
    google: 'DM Serif Display',
  },
  {
    id: 'cormorant-garamond',
    name: 'Cormorant Garamond',
    family: "'Cormorant Garamond'",
    category: 'serif',
    google: 'Cormorant Garamond',
  },
  {
    id: 'jetbrains-mono',
    name: 'JetBrains Mono',
    family: "'JetBrains Mono'",
    category: 'mono',
    google: 'JetBrains Mono',
  },
  {
    id: 'fira-code',
    name: 'Fira Code',
    family: "'Fira Code'",
    category: 'mono',
    google: 'Fira Code',
  },
  {
    id: 'ibm-plex-mono',
    name: 'IBM Plex Mono',
    family: "'IBM Plex Mono'",
    category: 'mono',
    google: 'IBM Plex Mono',
  },
  {
    id: 'space-mono',
    name: 'Space Mono',
    family: "'Space Mono'",
    category: 'mono',
    google: 'Space Mono',
  },
  {
    id: 'source-code-pro',
    name: 'Source Code Pro',
    family: "'Source Code Pro'",
    category: 'mono',
    google: 'Source Code Pro',
  },
  {
    id: 'geist-mono',
    name: 'Geist Mono',
    family: "'Geist Mono'",
    category: 'mono',
    google: 'Geist Mono',
  },
  {
    id: 'bricolage-grotesque',
    name: 'Bricolage Grotesque',
    family: "'Bricolage Grotesque'",
    category: 'display',
    google: 'Bricolage Grotesque',
  },
  { id: 'syne', name: 'Syne', family: "'Syne'", category: 'display', google: 'Syne' },
  {
    id: 'unbounded',
    name: 'Unbounded',
    family: "'Unbounded'",
    category: 'display',
    google: 'Unbounded',
  },
  {
    id: 'big-shoulders-display',
    name: 'Big Shoulders Display',
    family: "'Big Shoulders Display'",
    category: 'display',
    google: 'Big Shoulders Display',
  },
  {
    id: 'quicksand',
    name: 'Quicksand',
    family: "'Quicksand'",
    category: 'rounded',
    google: 'Quicksand',
  },
  {
    id: 'comfortaa',
    name: 'Comfortaa',
    family: "'Comfortaa'",
    category: 'rounded',
    google: 'Comfortaa',
  },
  {
    id: 'varela-round',
    name: 'Varela Round',
    family: "'Varela Round'",
    category: 'rounded',
    google: 'Varela Round',
  },
  {
    id: 'm-plus-rounded-1c',
    name: 'M PLUS Rounded 1c',
    family: "'M PLUS Rounded 1c'",
    category: 'rounded',
    google: 'M PLUS Rounded 1c',
  },
  { id: 'fredoka', name: 'Fredoka', family: "'Fredoka'", category: 'rounded', google: 'Fredoka' },
] as const;

/** Id of any font in the catalog. */
export type FontId = (typeof FONT_DATA)[number]['id'];

/** Every font the user can pick, in catalog order. */
export const FONT_PRESETS: readonly FontPreset[] = FONT_DATA;

/** Font ids in catalog order. */
export const FONT_IDS: readonly FontId[] = FONT_DATA.map((preset) => preset.id);

/** How many fonts the Appearance row shows before "See all". */
export const FONT_ROW_LIMIT = 7;

/** The weight a font tile's "Aa" is drawn in (one of the preview weights ThemeService loads). */
export const FONT_PREVIEW_WEIGHT = 500;

/**
 * The name the platform gives its system font, from the user agent, so the
 * `system` preset can read "System (Segoe UI)"; `null` when it is not known.
 */
export function systemFontName(userAgent: string): string | null {
  if (/Android/i.test(userAgent)) return 'Roboto';
  if (/iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(userAgent)) return 'San Francisco';
  if (/Windows/i.test(userAgent)) return 'Segoe UI';
  return null;
}

/**
 * Glass effect: `on` paints the floating chrome as frosted glass; `off` makes
 * those surfaces solid.
 */
export const GLASS_IDS = ['on', 'off'] as const;
export type GlassId = (typeof GLASS_IDS)[number];

/** The full set of appearance preferences. */
export type AppearancePrefs = {
  theme: ThemeChoice;
  accent: AccentId;
  background: BackgroundId;
  pattern: PatternId;
  glass: GlassId;
  font: FontId;
};

/** What a user with no saved preferences gets. */
export const APPEARANCE_DEFAULTS = {
  theme: 'system',
  accent: 'blue',
  background: 'none',
  pattern: 'dots',
  glass: 'on',
  font: 'geist',
} as const satisfies AppearancePrefs;

/**
 * Old `theme-preferences` accent names mapped to the new accent ids. Anything
 * not listed falls back to the default accent.
 */
export const LEGACY_ACCENT_MAP: Record<string, AccentId> = {
  default: 'blue',
  // The brand-blue preset of the first redesign build: folded into Azul.
  primecontrol: 'blue',
  blue: 'blue',
  green: 'green',
  yellow: 'yellow',
  pink: 'pink',
  orange: 'orange',
  purple: 'violet',
  black: 'graphite',
};

/**
 * Old `backgroundPattern` names mapped to the new texture ids. Anything not
 * listed falls back to the default texture.
 */
export const LEGACY_PATTERN_MAP: Record<string, PatternId> = {
  dots: 'dots',
  grid: 'grid',
  topography: 'topography',
  morphingDiamonds: 'diamonds',
  none: 'none',
};

/** True when `value` is an accent id of the catalog. */
export const isAccentId = (value: unknown): value is AccentId =>
  typeof value === 'string' && (ACCENT_IDS as readonly string[]).includes(value);

/** True when `value` is a background id of the catalog (`none` included). */
export const isBackgroundId = (value: unknown): value is BackgroundId =>
  typeof value === 'string' && (BACKGROUND_IDS as readonly string[]).includes(value);

/** True when `value` is a texture id (`none` included). */
export const isPatternId = (value: unknown): value is PatternId =>
  typeof value === 'string' && (PATTERN_IDS as readonly string[]).includes(value);

/** True when `value` is a font id of the catalog. */
export const isFontId = (value: unknown): value is FontId =>
  typeof value === 'string' && (FONT_IDS as readonly string[]).includes(value);

/** True when `value` is `system`, `light` or `dark`. */
export const isThemeChoice = (value: unknown): value is ThemeChoice =>
  typeof value === 'string' && (THEME_CHOICES as readonly string[]).includes(value);

/** True when `value` is `on` or `off`. */
export const isGlassId = (value: unknown): value is GlassId =>
  typeof value === 'string' && (GLASS_IDS as readonly string[]).includes(value);

/** What the browser shows while a family loads, or if it never does, per category. */
const FONT_FALLBACKS: Record<FontCategory, string> = {
  sans: 'system-ui, sans-serif',
  serif: 'ui-serif, Georgia, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  display: 'system-ui, sans-serif',
  rounded: 'ui-rounded, system-ui, sans-serif',
};

/**
 * The CSS font stack for a preset: its family followed by the fallback for its
 * category, so text stays readable while the font loads or if it never does.
 */
export function fontStack(font: FontPreset): string {
  return `${font.family}, ${FONT_FALLBACKS[font.category]}`;
}
